/**
 * @class
 * @function Predictive Search
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.search-drawer--content');
      this.closeBtn = this.querySelector('.header-inline-search__close');
      this.resetBtn = this.querySelector('.header-inline-search__reset'); 
      
      if (!this.predictiveSearchResults) {
        const drawer = document.getElementById('Search-Drawer');
        if (drawer) {
          this.predictiveSearchResults = drawer.querySelector('.search-drawer--content');
        }
      }
      if (!this.form || !this.input) {
        return;
      }
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', () => {
        this.syncSearchControls();
      });

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('click', this.onFocus.bind(this));

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.onCloseBtnClick.bind(this));
      }
      if (this.resetBtn) {
        this.resetBtn.addEventListener('mousedown', (event) => event.preventDefault());
        this.resetBtn.addEventListener('click', this.onResetBtnClick.bind(this));
      }

      this.syncSearchControls();

      // Close dropdown when clicking outside this instance only.
      document.addEventListener('click', (event) => {
        const target = event.target;
        if (this.contains(target)) {
          return;
        }
        // Click landed in another header search UI (e.g. desktop inline while this is mobile).
        // That is outside `this` but must not run this.close() for unrelated forms.
        const otherInline = target.closest('search-form.header-inline-search--desktop');
        if (otherInline && otherInline !== this) {
          return;
        }
        this.close();
      });

      this.setupOpenSearchTriggers();
    }

    setupOpenSearchTriggers() {
      if (!this.classList.contains('header-inline-search--desktop')) return;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('a[href="#open-search"]');
        if (!trigger) return;
        event.preventDefault();
        this.input.focus({ preventScroll: true });
      });
    }

    syncSearchControls() {
      const hasQuery = this.getQuery().length > 0;
      const isActive = this.classList.contains('is-active');
      this.classList.toggle('has-query', hasQuery);

      if (this.closeBtn) {
        this.closeBtn.hidden = !(isActive || hasQuery);
      }
      if (this.resetBtn) {
        this.resetBtn.hidden = !hasQuery;
      }
    }

    clearQuery() {
      this.input.value = '';
      this.syncSearchControls();

      if (!this.predictiveSearchResults) {
        return;
      }

      const resultsContent = this.predictiveSearchResults.querySelector('.search-drawer--results');
      const defaultContent = this.predictiveSearchResults.querySelector('.search-drawer--default');

      if (resultsContent) {
        resultsContent.innerHTML = '';
        resultsContent.style.display = 'none';
        if (defaultContent) defaultContent.style.display = 'block';
      } else {
        this.predictiveSearchResults.innerHTML = '';
      }
      this.predictiveSearchResults.classList.remove('loading');

      if (this.classList.contains('is-active')) {
        this.predictiveSearchResults.style.display = 'block';
      }
    }

    onCloseBtnClick(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.clearQuery();
      if (this.input) this.input.blur();
      this.close();
    }

    onResetBtnClick(event) {
      if (event) event.preventDefault();
      this.clearQuery();
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      this.syncSearchControls();
      if (!this.predictiveSearchResults) {
        return;
      }
      const searchTerm = this.getQuery();
      const defaultContent = this.predictiveSearchResults.querySelector('.search-drawer--default');
      const resultsContent = this.predictiveSearchResults.querySelector('.search-drawer--results');

      this.predictiveSearchResults.style.display = 'block';

      if (!searchTerm.length) {
        if (defaultContent) defaultContent.style.display = 'block';
        if (resultsContent) {
          resultsContent.innerHTML = '';
          resultsContent.style.display = 'none'; 
        } else {
          this.predictiveSearchResults.innerHTML = '';
        }
        this.predictiveSearchResults.classList.remove('loading');
        return;
      }

      if (defaultContent) defaultContent.style.display = 'none';
      if (resultsContent) resultsContent.style.display = 'block';
      this.predictiveSearchResults.classList.add('loading');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    setStyle1HeaderSearchActive(active) {
      if (!this.classList.contains('header-inline-search--desktop')) {
        return;
      }
      const header = this.closest('.header.style1');
      if (!header) return;
      header.classList.toggle('jsc-inline-search-active', Boolean(active));
    }

    onFocus() {
      this.classList.add('is-active');
      this.setStyle1HeaderSearchActive(true);
      this.syncSearchControls();

      const searchTerm = this.getQuery();
      const defaultContent = this.predictiveSearchResults ? this.predictiveSearchResults.querySelector('.search-drawer--default') : null;

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'block';
      }

      if (!searchTerm.length) {
        if (defaultContent) defaultContent.style.display = 'block';
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
      if (!this.predictiveSearchResults) {
        return;
      }

      this.predictiveSearchResults.classList.add('loading');

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          this.predictiveSearchResults.classList.remove('loading');
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const sectionEl = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          if (!sectionEl) {
            return;
          }
          const resultsMarkup = sectionEl.innerHTML;

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) {
        return;
      }

      // SAFETY CHECK: If the user cleared the box before the search finished, do not render!
      if (!this.getQuery().length) {
        return;
      }

      const resultsContent = this.predictiveSearchResults.querySelector('.search-drawer--results');
      if (resultsContent) {
        resultsContent.innerHTML = resultsMarkup;
        resultsContent.style.display = 'block';
      } else {
        this.predictiveSearchResults.innerHTML = resultsMarkup;
      }
      this.predictiveSearchResults.classList.remove('loading');

      // Inject popular searches below suggestions in the left column
      const defaultContent = this.predictiveSearchResults.querySelector('.search-drawer--default');
      const defaultPopular = defaultContent ? defaultContent.querySelector('.search-drawer--popular') : null;
      const resultsPopular = resultsContent ? resultsContent.querySelector('.search-drawer--popular') : null;
      if (resultsPopular && defaultPopular) {
        const popularCopy = defaultPopular.cloneNode(true);
        popularCopy.classList.add('search-drawer--popular-injected');
        resultsPopular.appendChild(popularCopy);
      }

      const highlightTarget = resultsContent || this.predictiveSearchResults;
      this.highlightSearchTerms(highlightTarget, this.getQuery());

      if (typeof ScrollTrigger === 'undefined') {
        return;
      }
      ScrollTrigger.batch(this.predictiveSearchResults.querySelectorAll('.animations-true .product-card--image-primary'), {
        start: "top 90%",
        onEnter: (elements) => {
          gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
        }
      });
      ScrollTrigger.batch(this.predictiveSearchResults.querySelectorAll('.animations-true .collection-card--link'), {
        start: "top 90%",
        onEnter: (elements) => {
          gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
        }
      });
    }
    
    highlightSearchTerms(container, searchTerm) {
      if (!container || !searchTerm) return;

      const terms = searchTerm
        .trim()
        .split(/\s+/)
        .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .filter(Boolean);
      if (!terms.length) return;

      const pattern = new RegExp(`(${terms.join('|')})`, 'gi');
      const selectors = [
        '.product-card-title',
        '.product-card--title',
        '.collection-card--text-link',
        '.collection-card--title',
      ].join(', ');

      container.querySelectorAll(selectors).forEach((el) => {
        this.highlightTextNodes(el, pattern);
      });
    }

    highlightTextNodes(element, pattern) {
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          if (!node.textContent || !node.textContent.trim()) {
            return NodeFilter.FILTER_REJECT;
          }
          if (node.parentNode && node.parentNode.nodeName === 'MARK') {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) textNodes.push(node);

      textNodes.forEach((textNode) => {
        const text = textNode.textContent;
        pattern.lastIndex = 0;
        if (!pattern.test(text)) return;

        pattern.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        while ((match = pattern.exec(text)) !== null) {
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          const mark = document.createElement('mark');
          mark.textContent = match[0];
          fragment.appendChild(mark);
          lastIndex = match.index + match[0].length;
          // Guard against zero-width matches.
          if (match.index === pattern.lastIndex) pattern.lastIndex++;
        }
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        textNode.parentNode.replaceChild(fragment, textNode);
      });
    }

    close() {
      const drawer = document.getElementById('Search-Drawer');
      if (drawer) {
        drawer.classList.remove('active');
      }
      this.classList.remove('is-active');
      this.setStyle1HeaderSearchActive(false);

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'none';
      }

      this.syncSearchControls();
    }
  }
  customElements.define('search-form', SearchForm);
}

if (!window.__jscHeaderSearchCloseBound) {
  window.__jscHeaderSearchCloseBound = true;
  document.addEventListener('click', (event) => {
    const closeBtn = event.target.closest('.header-inline-search__close');
    if (!closeBtn) return;
    const searchEl = closeBtn.closest('search-form');
    if (!searchEl) return;

    event.preventDefault();
    event.stopPropagation();

    const input = searchEl.querySelector('input[type="search"]');
    if (input) {
      input.value = '';
      input.blur();
    }

    if (typeof searchEl.close === 'function') {
      if (typeof searchEl.clearQuery === 'function') searchEl.clearQuery();
      searchEl.close();
      return;
    }

    searchEl.classList.remove('is-active', 'has-query');
    const header = searchEl.closest('.header.style1');
    if (header) header.classList.remove('jsc-inline-search-active');
    const predictive = searchEl.querySelector('.search-drawer--content');
    if (predictive) predictive.style.display = 'none';
    const resetBtn = searchEl.querySelector('.header-inline-search__reset');
    closeBtn.hidden = true;
    if (resetBtn) resetBtn.hidden = true;
  }, true);
}